import * as THREE from 'three';
import { perf, type PerfSnapshot } from './PerfDiag';
import type { GpuStats, PerfGpuTimer } from './GpuTimer';
import { collectSceneAudit, type SceneAuditSnapshot } from './SceneAudit';
import type { AppRenderer } from '../../render/Renderer';

export interface PerfLabRuntimeInfo {
  mode: string;
  reducedRenderLoad: boolean;
  /** When `reducedRenderLoad` first flipped true in this session, in `performance.now()` ms. */
  reducedRenderLoadAt?: number | null;
  /** Human-readable reason captured at the moment `reducedRenderLoad` flipped true. */
  reducedRenderLoadReason?: string | null;
  renderedFrameCount: number;
  stepCount: number;
  /** Total RAF callbacks that did not draw because the render cap was active. */
  renderSkipCount?: number;
  renderHzCap?: number | 'uncapped';
  autoFrameCap?: boolean;
  drs: unknown;
}

export interface PerfLabOptions {
  includeScene?: boolean;
}

export interface PerfLabSnapshot {
  t: number;
  runtime: PerfLabRuntimeInfo;
  renderer: RendererLabSnapshot;
  cpu: CpuLabSnapshot;
  gpu: GpuLabSnapshot;
  memory: MemoryLabSnapshot | null;
  longTasks: LongTaskSnapshot[];
  scene?: SceneAuditSnapshot;
}

export interface PerfLabTrace {
  startedAt: number;
  endedAt: number;
  durationMs: number;
  raf: TimingStats;
  start: PerfLabSnapshot;
  end: PerfLabSnapshot;
  samples: PerfLabSnapshot[];
  summary: PerfLabTraceSummary;
}

export interface PerfLabTraceSummary {
  /**
   * Mean RAF callback rate. Counts every requestAnimationFrame tick — even
   * ticks that were skipped by the render cap. This is the "display
   * cadence" view of FPS and usually pins to the monitor's refresh rate.
   */
  fpsMean: number;
  frameP95Ms: number;
  /**
   * Mean rate of *drawn* frames. This is what PerfHud shows and what the
   * player perceives. When the render cap is skipping frames (or DRS /
   * adaptive-quality reduction is gating renders), this drops below
   * `fpsMean` even when the loop appears smooth. The two diverging is the
   * cleanest signal that the cap is fighting the monitor rate.
   */
  renderedFpsMean: number;
  /** How many RAF ticks chose not to draw during this trace. */
  renderSkipCount: number;
  /** Skipped / (skipped + drawn). 0 = every RAF drew. */
  renderSkipRatio: number;
  cpuTop: MetricRow[];
  gpuTop: MetricRow[];
  drawCalls: number;
  triangles: number;
  dpr: number;
  reducedRenderLoad: boolean;
  longTaskCount: number;
  longTaskTotalMs: number;
}

interface PerfLabContext {
  renderer: AppRenderer;
  scene: THREE.Scene;
  gpuTimer: PerfGpuTimer | null;
  getRuntime: () => PerfLabRuntimeInfo;
}

interface RendererLabSnapshot {
  pixelRatio: number;
  canvas: {
    clientWidth: number;
    clientHeight: number;
    drawingBufferWidth: number;
    drawingBufferHeight: number;
  };
  render: {
    calls: number;
    triangles: number;
    points: number;
    lines: number;
    frame: number;
  };
  memory: {
    geometries: number;
    textures: number;
  };
  programs: number | null;
  shadowMapEnabled: boolean;
  shadowMapAutoUpdate: boolean;
  shadowMapNeedsUpdate: boolean;
}

interface CpuLabSnapshot {
  frameDtMs: number;
  frameStats: TimingStats;
  uncategorizedMs: number;
  phases: MetricRow[];
  currentPhases: MetricRow[];
  events: PerfSnapshot['events'];
}

interface GpuLabSnapshot {
  available: boolean;
  totalMeanMs: number;
  rows: MetricRow[];
}

interface MemoryLabSnapshot {
  usedJSHeapMB: number;
  totalJSHeapMB: number;
  jsHeapLimitMB: number;
}

interface LongTaskAttribution {
  name: string;
  entryType: string;
  containerType: string;
  containerSrc: string;
  containerId: string;
}

interface LongTaskSnapshot {
  name: string;
  startTime: number;
  duration: number;
  attribution: LongTaskAttribution[];
}

interface MetricRow {
  name: string;
  mean: number;
  p95: number;
  max: number;
  count: number;
}

interface TimingStats {
  samples: number;
  mean: number;
  p50: number;
  p95: number;
  max: number;
}

const LONG_TASK_LIMIT = 120;

export class PerfLab {
  private readonly renderer: AppRenderer;
  private readonly scene: THREE.Scene;
  private readonly gpuTimer: PerfGpuTimer | null;
  private readonly getRuntime: () => PerfLabRuntimeInfo;
  private readonly longTasks: LongTaskSnapshot[] = [];
  private observer: PerformanceObserver | null = null;

  constructor(ctx: PerfLabContext) {
    this.renderer = ctx.renderer;
    this.scene = ctx.scene;
    this.gpuTimer = ctx.gpuTimer;
    this.getRuntime = ctx.getRuntime;
    this.installLongTaskObserver();
  }

  snapshot(options: PerfLabOptions = {}): PerfLabSnapshot {
    const perfSnapshot = perf.snapshot();
    const renderer = rendererSnapshot(this.renderer);
    const out: PerfLabSnapshot = {
      t: performance.now(),
      runtime: this.getRuntime(),
      renderer,
      cpu: cpuSnapshot(perfSnapshot),
      gpu: gpuSnapshot(this.gpuTimer),
      memory: memorySnapshot(),
      longTasks: [...this.longTasks],
    };
    if (options.includeScene) out.scene = collectSceneAudit(this.scene, this.renderer);
    return out;
  }

  trace(options: { durationMs?: number; sampleIntervalMs?: number; includeScene?: boolean } = {}): Promise<PerfLabTrace> {
    const durationMs = clampNumber(options.durationMs, 5000, 250, 30000);
    const sampleIntervalMs = clampNumber(options.sampleIntervalMs, 250, 50, 2000);
    const includeScene = options.includeScene ?? true;
    const startedAt = performance.now();
    const start = this.snapshot({ includeScene });
    const samples: PerfLabSnapshot[] = [];
    const rafDts: number[] = [];
    let lastRaf = startedAt;
    let nextSample = startedAt;

    return new Promise((resolve) => {
      const tick = (now: number): void => {
        rafDts.push(now - lastRaf);
        lastRaf = now;
        if (now >= nextSample) {
          samples.push(this.snapshot({ includeScene: false }));
          nextSample = now + sampleIntervalMs;
        }
        if (now - startedAt >= durationMs) {
          const endedAt = performance.now();
          const end = this.snapshot({ includeScene });
          resolve({
            startedAt,
            endedAt,
            durationMs: endedAt - startedAt,
            raf: summarizeTiming(rafDts.slice(1)),
            start,
            end,
            samples,
            summary: traceSummary(rafDts.slice(1), start, end, endedAt - startedAt),
          });
          return;
        }
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  destroy(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private installLongTaskObserver(): void {
    try {
      this.observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          // PerformanceLongTaskTiming exposes attribution with container info
          // (iframe vs same-page) and the script that ran the long-task.
          // Browsers vary in what they populate; default everything to ''.
          const attrSource = ((entry as unknown as { attribution?: Array<{
            name?: string;
            entryType?: string;
            containerType?: string;
            containerSrc?: string;
            containerId?: string;
          }> }).attribution) ?? [];
          const attribution: LongTaskAttribution[] = attrSource.map((a) => ({
            name: a.name ?? '',
            entryType: a.entryType ?? '',
            containerType: a.containerType ?? '',
            containerSrc: a.containerSrc ?? '',
            containerId: a.containerId ?? '',
          }));
          this.longTasks.push({
            name: entry.name,
            startTime: entry.startTime,
            duration: entry.duration,
            attribution,
          });
          if (this.longTasks.length > LONG_TASK_LIMIT) this.longTasks.shift();
        }
      });
      this.observer.observe({ entryTypes: ['longtask'] });
    } catch {
      this.observer = null;
    }
  }
}

export class PerfLabPanel {
  private readonly lab: PerfLab;
  private readonly el: HTMLDivElement;
  private readonly summaryEl: HTMLDivElement;
  private readonly cpuEl: HTMLDivElement;
  private readonly gpuEl: HTMLDivElement;
  private readonly sceneEl: HTMLDivElement;
  private readonly eventsEl: HTMLDivElement;
  private lastTrace: PerfLabTrace | PerfLabSnapshot | null = null;
  private lastUpdate = 0;
  private lastScene: SceneAuditSnapshot | undefined;

  constructor(lab: PerfLab) {
    this.lab = lab;
    this.el = document.createElement('div');
    this.el.style.cssText = `
      position: fixed; right: 8px; top: 8px; z-index: 2147483647;
      width: min(620px, calc(100vw - 16px)); max-height: calc(100vh - 16px);
      overflow: auto; box-sizing: border-box; pointer-events: auto;
      font: 11px/1.35 ui-monospace, Menlo, Consolas, monospace;
      background: rgba(2, 7, 11, 0.9); color: #d8f7ff; padding: 10px;
      border: 1px solid rgba(92, 193, 218, 0.65); border-radius: 4px;
      box-shadow: 0 12px 28px rgba(0,0,0,0.4);
    `;

    const header = document.createElement('div');
    header.style.cssText = 'display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom:8px;';
    const title = document.createElement('div');
    title.style.cssText = 'font-weight:700; color:#ffffff;';
    title.textContent = 'Perf Lab';
    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex; gap:6px;';
    actions.append(
      this.button('Trace 5s', () => this.runTrace()),
      this.button('JSON', () => this.downloadJson()),
      this.button('Hide', () => { this.el.hidden = true; }),
    );
    header.append(title, actions);
    this.el.appendChild(header);

    this.summaryEl = this.section();
    this.cpuEl = this.section();
    this.gpuEl = this.section();
    this.sceneEl = this.section();
    this.eventsEl = this.section();
    this.el.append(this.summaryEl, this.cpuEl, this.gpuEl, this.sceneEl, this.eventsEl);
    document.body.appendChild(this.el);
  }

  tick(): void {
    const now = performance.now();
    if (now - this.lastUpdate < 250) return;
    this.lastUpdate = now;
    const includeScene = !this.lastScene || now % 1250 < 300;
    const snapshot = this.lab.snapshot({ includeScene });
    this.lastTrace = snapshot;
    if (snapshot.scene) this.lastScene = snapshot.scene;
    this.render(snapshot);
  }

  destroy(): void {
    this.el.remove();
  }

  show(): void {
    this.el.hidden = false;
  }

  private render(snapshot: PerfLabSnapshot): void {
    const runtime = snapshot.runtime;
    const renderer = snapshot.renderer;
    const fps = snapshot.cpu.frameStats.mean > 0 ? 1000 / snapshot.cpu.frameStats.mean : 0;
    const reducedFragment = runtime.reducedRenderLoad
      ? `reduced (${runtime.reducedRenderLoadReason ?? 'unknown'} @ ${runtime.reducedRenderLoadAt ? Math.round(runtime.reducedRenderLoadAt) + 'ms' : '?'})`
      : 'full';
    this.summaryEl.textContent = [
      `mode ${runtime.mode}  quality ${reducedFragment}  fps ${fps.toFixed(1)}  frame p95 ${snapshot.cpu.frameStats.p95.toFixed(2)}ms`,
      `dpr ${renderer.pixelRatio.toFixed(2)}  buffer ${renderer.canvas.drawingBufferWidth}x${renderer.canvas.drawingBufferHeight}  draws ${renderer.render.calls}  tris ${formatCount(renderer.render.triangles)}  programs ${renderer.programs ?? 0}`,
      `heap ${snapshot.memory ? `${snapshot.memory.usedJSHeapMB.toFixed(0)}MB` : 'n/a'}  shadows ${renderer.shadowMapEnabled ? 'on' : 'off'} auto ${renderer.shadowMapAutoUpdate ? 'on' : 'off'}`,
    ].join('\n');

    this.cpuEl.textContent = [
      'CPU phases (ms, p95 sorted)',
      ...snapshot.cpu.phases.slice(0, 18).map(formatMetricRow),
    ].join('\n');

    this.gpuEl.textContent = snapshot.gpu.available
      ? [
        `GPU timers (ms, p95 sorted) totalMean ${snapshot.gpu.totalMeanMs.toFixed(2)}`,
        ...snapshot.gpu.rows.slice(0, 18).map(formatMetricRow),
      ].join('\n')
      : 'GPU timers unavailable';

    const scene = snapshot.scene ?? this.lastScene;
    this.sceneEl.textContent = scene
      ? [
        `Scene nodes ${scene.scene.totalNodes}  meshes ${scene.scene.visibleMeshes}/${scene.scene.meshes}  lods ${scene.scene.lods}`,
        `estimated visible draws ${scene.scene.visibleEstimatedDrawCalls}  visible tris ${formatCount(scene.scene.visibleTriangles)}  frustumCulled=false ${scene.scene.frustumCulledFalseMeshes}`,
        'Top meshes by triangles',
        ...scene.topMeshesByTriangles.slice(0, 8).map((mesh) => (
          `${mesh.triangles.toString().padStart(8)} tri  ${mesh.drawCalls.toString().padStart(3)} draw  ${mesh.name}  ${mesh.path}`
        )),
      ].join('\n')
      : 'Scene audit pending';

    const longTaskTotal = snapshot.longTasks.reduce((sum, task) => sum + task.duration, 0);
    this.eventsEl.textContent = [
      `Long tasks ${snapshot.longTasks.length} total ${longTaskTotal.toFixed(1)}ms`,
      'Events',
      ...snapshot.cpu.events.slice(0, 8).map((event) => `${event.kind} ${event.data ? JSON.stringify(event.data) : ''}`),
    ].join('\n');
  }

  private section(): HTMLDivElement {
    const el = document.createElement('div');
    el.style.cssText = 'white-space: pre; margin-bottom: 10px;';
    return el;
  }

  private button(label: string, onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.style.cssText = `
      font: inherit; color: #d8f7ff; background: rgba(0, 182, 238, 0.12);
      border: 1px solid rgba(92, 193, 218, 0.65); border-radius: 3px;
      padding: 3px 7px; cursor: pointer;
    `;
    button.addEventListener('click', onClick);
    return button;
  }

  private async runTrace(): Promise<void> {
    this.summaryEl.textContent = 'Tracing 5s...';
    const trace = await this.lab.trace({ durationMs: 5000, sampleIntervalMs: 250, includeScene: true });
    this.lastTrace = trace;
    this.render(trace.end);
    const skipPct = (trace.summary.renderSkipRatio * 100).toFixed(1);
    this.eventsEl.textContent = [
      `Trace complete: rAF ${trace.summary.fpsMean.toFixed(1)}fps  rendered ${trace.summary.renderedFpsMean.toFixed(1)}fps  skipped ${trace.summary.renderSkipCount} (${skipPct}%)  p95 ${trace.summary.frameP95Ms.toFixed(2)}ms`,
      `CPU top ${trace.summary.cpuTop.slice(0, 4).map((row) => `${row.name}:${row.p95.toFixed(2)}`).join('  ')}`,
      `GPU top ${trace.summary.gpuTop.slice(0, 4).map((row) => `${row.name}:${row.p95.toFixed(2)}`).join('  ')}`,
    ].join('\n');
  }

  private downloadJson(): void {
    const payload = this.lastTrace ?? this.lab.snapshot({ includeScene: true });
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `perf-lab-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 5000);
  }
}

function rendererSnapshot(renderer: AppRenderer): RendererLabSnapshot {
  const drawingBufferSize = new THREE.Vector2();
  renderer.getDrawingBufferSize(drawingBufferSize);
  return {
    pixelRatio: renderer.getPixelRatio(),
    canvas: {
      clientWidth: renderer.domElement.clientWidth,
      clientHeight: renderer.domElement.clientHeight,
      drawingBufferWidth: drawingBufferSize.x,
      drawingBufferHeight: drawingBufferSize.y,
    },
    render: {
      calls: rendererDrawCalls(renderer.info),
      triangles: renderer.info.render.triangles,
      points: renderer.info.render.points,
      lines: renderer.info.render.lines,
      frame: rendererFrame(renderer.info),
    },
    memory: {
      geometries: renderer.info.memory.geometries,
      textures: renderer.info.memory.textures,
    },
    programs: rendererProgramCount(renderer.info),
    shadowMapEnabled: renderer.shadowMap.enabled,
    shadowMapAutoUpdate: rendererShadowFlag(renderer, 'autoUpdate'),
    shadowMapNeedsUpdate: rendererShadowFlag(renderer, 'needsUpdate'),
  };
}

function rendererFrame(info: AppRenderer['info']): number {
  return (info as { render?: { frame?: number }; frame?: number }).render?.frame
    ?? (info as { frame?: number }).frame
    ?? 0;
}

export function rendererDrawCalls(info: AppRenderer['info']): number {
  const render = info.render as { calls?: number; drawCalls?: number };
  return typeof render.drawCalls === 'number' ? render.drawCalls : render.calls ?? 0;
}

function rendererProgramCount(info: AppRenderer['info']): number | null {
  const webglPrograms = (info as { programs?: unknown[] }).programs;
  if (Array.isArray(webglPrograms)) return webglPrograms.length;
  return (info as { memory?: { programs?: number } }).memory?.programs ?? null;
}

function rendererShadowFlag(renderer: AppRenderer, key: 'autoUpdate' | 'needsUpdate'): boolean {
  return Boolean((renderer.shadowMap as Record<string, unknown>)[key]);
}

function cpuSnapshot(snapshot: PerfSnapshot): CpuLabSnapshot {
  const frameStats = summarizeTiming(snapshot.frames.map((frame) => frame.dtMs).filter((dt) => dt > 0));
  return {
    frameDtMs: snapshot.frameDtMs,
    frameStats,
    uncategorizedMs: snapshot.uncategorizedMs,
    phases: Object.entries(snapshot.phaseStats)
      .map(([name, stats]) => ({ name, ...stats, count: snapshot.frames.filter((frame) => frame.phases[name] !== undefined).length }))
      .sort((a, b) => b.p95 - a.p95),
    currentPhases: Object.entries(snapshot.currentPhases)
      .map(([name, value]) => ({ name, mean: value, p95: value, max: value, count: 1 }))
      .sort((a, b) => b.mean - a.mean),
    events: snapshot.events,
  };
}

function gpuSnapshot(gpuTimer: PerfGpuTimer | null): GpuLabSnapshot {
  if (!gpuTimer) return { available: false, totalMeanMs: 0, rows: [] };
  const stats = gpuTimer.stats();
  return {
    available: gpuTimer.available(),
    totalMeanMs: gpuTimer.totalMean(),
    rows: Object.entries(stats)
      .map(([name, row]: [string, GpuStats]) => ({ name, mean: row.mean, p95: row.p95, max: row.max, count: row.count }))
      .sort((a, b) => b.p95 - a.p95),
  };
}

function memorySnapshot(): MemoryLabSnapshot | null {
  const mem = (performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
  }).memory;
  if (!mem) return null;
  return {
    usedJSHeapMB: mem.usedJSHeapSize / 1024 / 1024,
    totalJSHeapMB: mem.totalJSHeapSize / 1024 / 1024,
    jsHeapLimitMB: mem.jsHeapSizeLimit / 1024 / 1024,
  };
}

function traceSummary(
  rafDts: number[],
  start: PerfLabSnapshot,
  end: PerfLabSnapshot,
  durationMs: number,
): PerfLabTraceSummary {
  const raf = summarizeTiming(rafDts);
  const longTaskTotalMs = end.longTasks.reduce((sum, task) => sum + task.duration, 0);
  const renderedFrames = Math.max(
    0,
    (end.runtime.renderedFrameCount ?? 0) - (start.runtime.renderedFrameCount ?? 0),
  );
  const renderSkipCount = Math.max(
    0,
    (end.runtime.renderSkipCount ?? 0) - (start.runtime.renderSkipCount ?? 0),
  );
  const rafTicks = renderedFrames + renderSkipCount;
  const renderedFpsMean = durationMs > 0 ? (renderedFrames * 1000) / durationMs : 0;
  const renderSkipRatio = rafTicks > 0 ? renderSkipCount / rafTicks : 0;
  return {
    fpsMean: raf.mean > 0 ? 1000 / raf.mean : 0,
    frameP95Ms: raf.p95,
    renderedFpsMean,
    renderSkipCount,
    renderSkipRatio,
    cpuTop: end.cpu.phases.slice(0, 8),
    gpuTop: end.gpu.rows.slice(0, 8),
    drawCalls: end.renderer.render.calls,
    triangles: end.renderer.render.triangles,
    dpr: end.renderer.pixelRatio,
    reducedRenderLoad: end.runtime.reducedRenderLoad,
    longTaskCount: end.longTasks.length,
    longTaskTotalMs,
  };
}

function summarizeTiming(values: number[]): TimingStats {
  if (values.length === 0) return { samples: 0, mean: 0, p50: 0, p95: 0, max: 0 };
  const sorted = [...values].sort((a, b) => a - b);
  const sum = values.reduce((a, b) => a + b, 0);
  return {
    samples: values.length,
    mean: sum / values.length,
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    max: sorted[sorted.length - 1] ?? 0,
  };
}

function percentile(sorted: number[], p: number): number {
  return sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * p))] ?? 0;
}

function clampNumber(value: number | undefined, fallback: number, min: number, max: number): number {
  if (!Number.isFinite(value ?? NaN)) return fallback;
  return Math.max(min, Math.min(max, value as number));
}

function formatMetricRow(row: MetricRow): string {
  const bar = '#'.repeat(Math.min(24, Math.round(row.p95)));
  return `${row.name.padEnd(26)} mean ${row.mean.toFixed(2).padStart(6)}  p95 ${row.p95.toFixed(2).padStart(6)}  max ${row.max.toFixed(2).padStart(6)}  n ${row.count.toString().padStart(3)}  ${bar}`;
}

function formatCount(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}m`;
  if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
  return `${value}`;
}
